import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search, Mail, Phone, Briefcase, User, Fingerprint, Calendar, HeartPulse, Shield, Home, Smartphone, Info, Clock, Edit2, X, Loader2, AlertCircle, RefreshCw, Plus, Save, LayoutGrid, List, CheckCircle, XCircle, ToggleLeft, ToggleRight, Lock, CreditCard, Users, Building2, Banknote, GraduationCap, Heart, Car, UserCircle, Camera, Trash2, BarChart3, DoorOpen, Calculator } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { employeeService, departmentService, positionService, shiftService } from '../services';
import type { Employee, Department, Position, EmployeeCreateDto, EmployeeUpdateDto, Shift, EmployeeShift } from '../services';
import { usePermission } from '../context/PermissionContext';
import SiteLocationFilter from '../components/SiteLocationFilter';
import siteService from '../services/siteService';
import locationService from '../services/locationService';
import { uyar, hata, onayla } from '../components/GlobalModal';

// Photo zoom modal - fotografi buyuk gosterir
const PhotoZoomModal: React.FC<{ photoUrl: string; name: string; onClose: () => void }> = ({ photoUrl, name, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 z-[100] backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={onClose}>
      <div className="relative max-w-lg w-full animate-in zoom-in-95 duration-300" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center text-slate-500 hover:text-red-500 shadow-lg z-10 transition-colors">
          <X size={18} />
        </button>
        <img src={photoUrl} alt={name} className="w-full rounded-2xl shadow-2xl object-contain max-h-[80vh]" />
        <div className="text-center mt-3">
          <span className="text-white/80 text-sm font-medium">{name}</span>
        </div>
      </div>
    </div>
  );
};

// Lazy photo component - fotografi sadece gorunur olunca yukler
const LazyPhoto: React.FC<{ employeeId: number; name: string; size?: string; rounded?: string; clickable?: boolean }> = ({ employeeId, name, size = 'w-20 h-20', rounded = 'rounded-[1.8rem]', clickable = true }) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [showZoom, setShowZoom] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [loaded]);

  useEffect(() => {
    if (!loaded) return;
    let cancelled = false;
    const fetchPhoto = async () => {
      try {
        const res = await employeeService.getPhoto(employeeId);
        if (!cancelled && res.success && res.data?.photoUrl) {
          setPhotoUrl(res.data.photoUrl);
        }
      } catch { /* ignore */ }
    };
    fetchPhoto();
    return () => { cancelled = true; };
  }, [loaded, employeeId]);

  const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const canClick = clickable && photoUrl;

  return (
    <>
      <div ref={imgRef} className={canClick ? 'cursor-pointer' : ''} onClick={() => canClick && setShowZoom(true)}>
        {photoUrl ? (
          <img src={photoUrl} alt={name} className={`${size} ${rounded} border-4 border-white shadow-md object-cover group-hover:scale-105 transition-transform duration-500`} />
        ) : (
          <div className={`${size} ${rounded} bg-blue-50 border-4 border-white shadow-md flex items-center justify-center text-blue-600 font-black text-xl group-hover:scale-105 transition-transform duration-500`}>
            {initials}
          </div>
        )}
      </div>
      {showZoom && photoUrl && <PhotoZoomModal photoUrl={photoUrl} name={name} onClose={() => setShowZoom(false)} />}
    </>
  );
};

// Email validation
const isValidEmail = (email: string): boolean => {
  if (!email) return true; // Email is optional
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// TC Kimlik validation - 11 digits only
const isValidTCKimlik = (tc: string): boolean => {
  if (!tc) return true; // TC is optional
  if (tc.length !== 11) return false;
  if (!/^\d{11}$/.test(tc)) return false;
  return true;
};

// Phone mask helper
const formatPhoneNumber = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 0) return '';
  if (numbers.length <= 3) return `(${numbers}`;
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  if (numbers.length <= 8) return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6, 8)} ${numbers.slice(8, 10)}`;
};

// Remove phone mask to get raw number
const unformatPhoneNumber = (value: string): string => {
  return value.replace(/\D/g, '');
};

interface EmployeeFormData {
  // Temel Bilgiler
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  departmentId: number;
  positionId: number;
  idNumber: string;
  hireDate: string;
  terminationDate?: string;
  birthDate: string;
  gender: string;
  address: string;
  isActive: boolean;

  // Kisisel Bilgiler
  maritalStatus: string;
  bloodType: string;
  militaryStatus: string;
  lastEducationLevel: string;
  profession: string;

  // Iletisim Bilgileri Extended
  homePhone: string;
  mobilePhone2: string;
  workPhone: string;
  workExtension: string;
  personalEmail: string;
  city: string;
  district: string;
  postalCode: string;

  // Acil Durum
  emergencyContact: string;
  emergencyPhone: string;

  // Aile Bilgileri
  spouseName: string;
  fatherName: string;
  motherName: string;
  childCount: string;

  // Finans & SGK
  socialSecurityNo: string;
  salary: string;
  bankName: string;
  ibanNo: string;

  // Izin Bilgileri
  annualLeaveDays: string;

  // Vardiya
  shiftId: string;

  // PDKS Device fields
  employeeCode: string;
  cardNumber: string;
  deviceUserId: string;
  devicePassword: string;
  hasFingerprint: boolean;
  hasCard: boolean;
  hasFace: boolean;
  cardType: string;
  privilege: number;

  // eID / MRZ
  mrzDocumentNumber: string;
  mrzDateOfBirth: string;
  mrzDateOfExpiry: string;

  // Site + Location (Ag 2026, cok santiyeli musteriler)
  siteId?: number | null | '';
  locationId?: number | null | '';
  // Mobil sifre (Ag 2026, self-check-in APK icin)
  password?: string;
}

interface FormModalProps {
  title: string;
  onSubmit: () => void;
  onClose: () => void;
  isEdit?: boolean;
  formData: EmployeeFormData;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onPhoneChange: (value: string) => void;
  departments: Department[];
  positions: Position[];
  shifts: Shift[];
  formError: string | null;
  saving: boolean;
  // Ag 2026: site/location secimi (customer sitesEnabled=true ise dolu gelir)
  sites?: { id: number; name: string }[];
  locationsForForm?: { id: number; name: string }[];
}

type FormTab = 'temel' | 'kisisel' | 'iletisim' | 'aile' | 'finans' | 'pdks';
type DetailTab = 'genel' | 'kisisel' | 'iletisim' | 'finans' | 'pdks';

interface EmployeeDetailPanelProps {
  employee: Employee;
  onClose: () => void;
  onEdit: () => void;
  getDepartmentName: (id: number) => string;
  onPhotoUpload: (employeeId: number, photoBase64: string | null) => Promise<void>;
  shifts: Shift[];
  onShiftAssign: (employeeId: number, shiftId: number | null) => Promise<void>;
}

const EmployeeDetailPanel: React.FC<EmployeeDetailPanelProps> = ({ employee, onClose, onEdit, getDepartmentName, onPhotoUpload, shifts, onShiftAssign }) => {
  const detailNavigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<DetailTab>('genel');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [localPhotoUrl, setLocalPhotoUrl] = useState<string | undefined>(employee.photoUrl);
  const [showPhotoZoom, setShowPhotoZoom] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const e = employee as any;

  // Lazy load photo for detail panel
  useEffect(() => {
    if (localPhotoUrl) return; // zaten var
    let cancelled = false;
    const loadPhoto = async () => {
      try {
        const res = await employeeService.getPhoto(employee.id);
        if (!cancelled && res.success && res.data?.photoUrl) {
          setLocalPhotoUrl(res.data.photoUrl);
        }
      } catch { /* ignore */ }
    };
    loadPhoto();
    return () => { cancelled = true; };
  }, [employee.id]);

  // Shift state
  const [employeeShift, setEmployeeShift] = useState<EmployeeShift | null>(null);
  const [loadingShift, setLoadingShift] = useState(true);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [savingShift, setSavingShift] = useState(false);

  // Load employee's current shift
  useEffect(() => {
    const loadShift = async () => {
      try {
        setLoadingShift(true);
        const result = await shiftService.getEmployeeShift(employee.id);
        if (result.success) {
          setEmployeeShift(result.data);
          const d = result.data as any;
          setSelectedShiftId(d?.shiftID || d?.ShiftID || d?.shiftId || null);
        }
      } catch (err) {
        console.error('Vardiya yuklenirken hata:', err);
      } finally {
        setLoadingShift(false);
      }
    };
    loadShift();
  }, [employee.id]);

  const handleShiftSave = async () => {
    setSavingShift(true);
    try {
      await onShiftAssign(employee.id, selectedShiftId);
      // Reload shift info
      const result = await shiftService.getEmployeeShift(employee.id);
      if (result.success) {
        setEmployeeShift(result.data);
      }
      setShowShiftModal(false);
    } catch (err) {
      console.error('Vardiya kaydetme hatasi:', err);
    } finally {
      setSavingShift(false);
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      uyar(t('employees.selectImage'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      uyar(t('employees.fileTooLarge'));
      return;
    }

    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result as string;
        await onPhotoUpload(employee.id, base64);
        setLocalPhotoUrl(base64);
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Photo upload error:', err);
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!(await onayla(t('employees.confirmDeletePhoto')))) return;
    setUploadingPhoto(true);
    try {
      await onPhotoUpload(employee.id, null);
      setLocalPhotoUrl(undefined);
    } catch (err) {
      console.error('Photo remove error:', err);
    }
    setUploadingPhoto(false);
  };

  const detailTabs: { id: DetailTab; label: string; icon: React.ReactNode }[] = [
    { id: 'genel', label: t('employees.general'), icon: <User size={14} /> },
    { id: 'kisisel', label: t('employees.personal'), icon: <UserCircle size={14} /> },
    { id: 'iletisim', label: t('employees.contactTab'), icon: <Phone size={14} /> },
    { id: 'finans', label: t('employees.finance'), icon: <Banknote size={14} /> },
    { id: 'pdks', label: 'PDKS', icon: <Fingerprint size={14} /> },
  ];

  const InfoRow = ({ label, value, icon }: { label: string; value?: string | number | null; icon?: React.ReactNode }) => (
    <div className="space-y-1">
      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
        {icon} {label}
      </label>
      <p className="text-sm font-bold text-slate-800">{value || t('employees.notEntered')}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[60] backdrop-blur-sm animate-in fade-in duration-300 flex justify-end">
      <div className="w-full max-w-2xl bg-white h-full shadow-2xl animate-in slide-in-from-right duration-500 flex flex-col relative">
        <button onClick={onClose} className="absolute top-6 right-6 p-2.5 bg-slate-50 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-full transition-all z-10">
          <X size={24} />
        </button>
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/* Header */}
          <div className="bg-slate-900 h-48 relative flex items-end px-10 pb-6">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className="relative flex items-center gap-6">
              <div className="relative group">
                {localPhotoUrl ? (
                  <img
                    src={localPhotoUrl}
                    alt={`${employee.firstName} ${employee.lastName}`}
                    className="w-24 h-24 rounded-3xl border-4 border-slate-900 shadow-2xl object-cover cursor-pointer"
                    onClick={() => setShowPhotoZoom(true)}
                  />
                ) : (
                  <div
                    className="w-24 h-24 rounded-3xl bg-blue-600 border-4 border-slate-900 shadow-2xl flex items-center justify-center text-white font-black text-3xl cursor-pointer"
                    onClick={handlePhotoClick}
                  >
                    {employee.firstName[0]}{employee.lastName[0]}
                  </div>
                )}
                {/* Upload overlay */}
                <div
                  className="absolute inset-0 rounded-3xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  onClick={handlePhotoClick}
                >
                  {uploadingPhoto ? (
                    <Loader2 size={24} className="text-white animate-spin" />
                  ) : (
                    <Camera size={24} className="text-white" />
                  )}
                </div>
                {/* Remove button */}
                {localPhotoUrl && !uploadingPhoto && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemovePhoto(); }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                    title={t('employees.deletePhoto')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-black text-white">{employee.firstName} {employee.lastName}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-md text-[10px] font-black uppercase tracking-widest border border-blue-500/30">
                    {employee.positionName || employee.positionTitle || t('employees.noPosition')}
                  </span>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest border ${employee.isActive ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}`}>
                    {employee.isActive ? t('employees.active') : t('employees.inactive')}
                  </span>
                  {e.bloodType && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-md text-[10px] font-black uppercase tracking-widest border border-red-500/30">
                      {e.bloodType}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-slate-100 px-8 bg-slate-50/50 sticky top-0 z-10">
            <div className="flex gap-1 -mb-px overflow-x-auto">
              {detailTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 bg-white'
                      : 'border-transparent text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="px-8 pt-4 flex gap-2">
            <button
              onClick={() => detailNavigate(`/attendance-logs?employeeId=${employee.id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-xs font-bold hover:bg-indigo-100 border border-indigo-200 transition-all"
            >
              <DoorOpen size={14} /> {t('employees.entryExitRecords')}
            </button>
            <button
              onClick={() => detailNavigate(`/timesheet?employeeId=${employee.id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-xs font-bold hover:bg-emerald-100 border border-emerald-200 transition-all"
            >
              <Calculator size={14} /> {t('employees.timesheet')}
            </button>
            <button
              onClick={() => detailNavigate(`/employee-stats?id=${employee.id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-xl text-xs font-bold hover:bg-purple-100 border border-purple-200 transition-all"
            >
              <BarChart3 size={14} /> {t('employees.statistics')}
            </button>
          </div>

          {/* Content */}
          <div className="p-8 space-y-8">
            {/* GENEL TAB */}
            {activeTab === 'genel' && (
              <>
                <div className="grid grid-cols-2 gap-6">
                  <InfoRow label={t('employees.idNumber')} value={employee.idNumber} icon={<Fingerprint size={12} />} />
                  <InfoRow label={t('employees.employeeIdLabel')} value={employee.employeeId} icon={<Smartphone size={12} />} />
                  <InfoRow label={t('employees.hireDateLabel')} value={employee.hireDate ? new Date(employee.hireDate).toLocaleDateString() : null} icon={<Calendar size={12} />} />
                  <InfoRow label={t('employees.birthDate')} value={employee.birthDate ? new Date(employee.birthDate).toLocaleDateString() : null} icon={<Calendar size={12} />} />
                  <InfoRow label={t('employees.departmentLabel')} value={employee.departmentName || getDepartmentName(employee.departmentId)} icon={<Building2 size={12} />} />
                  <InfoRow label={t('employees.positionLabel')} value={employee.positionName || employee.positionTitle} icon={<Briefcase size={12} />} />
                </div>
                {e.annualLeaveDays && (
                  <div className="border-t border-slate-100 pt-6">
                    <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4">{t('employees.leaveInfo')}</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <InfoRow label={t('employees.annualLeaveEntitlement')} value={`${e.annualLeaveDays} ${t('employees.daysUnit')}`} icon={<Calendar size={12} />} />
                      <InfoRow label={t('employees.remainingLeave')} value={employee.leaveBalance ? `${employee.leaveBalance} ${t('employees.daysUnit')}` : null} icon={<Calendar size={12} />} />
                    </div>
                  </div>
                )}

                {/* Vardiya Bilgileri */}
                <div className="border-t border-slate-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest flex items-center gap-2">
                      <Clock size={14} /> {t('employees.shiftInfo')}
                    </h4>
                    <button
                      onClick={() => setShowShiftModal(true)}
                      className="text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1"
                    >
                      <Edit2 size={12} /> {t('employees.change')}
                    </button>
                  </div>
                  {loadingShift ? (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Loader2 size={16} className="animate-spin" /> {t('employees.loading')}
                    </div>
                  ) : employeeShift ? (
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-slate-800">{employeeShift.shiftName}</p>
                          <p className="text-sm text-slate-500 mt-1">
                            {employeeShift.startTime} - {employeeShift.endTime}
                            {employeeShift.workHours && <span className="text-slate-400 ml-2">({employeeShift.workHours} {t('employees.hours')})</span>}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-black text-slate-400 uppercase">{t('employees.startDate')}</p>
                          <p className="text-sm font-bold text-slate-600">{new Date(employeeShift.startDate).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-center">
                      <Clock size={24} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-sm text-slate-400">{t('employees.noShiftAssigned')}</p>
                      <button
                        onClick={() => setShowShiftModal(true)}
                        className="mt-2 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        {t('employees.assignShift')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* KISISEL TAB */}
            {activeTab === 'kisisel' && (
              <>
                <div className="grid grid-cols-2 gap-6">
                  <InfoRow label={t('employees.gender')} value={e.gender === 'E' ? t('employees.male') : e.gender === 'K' ? t('employees.female') : e.gender} icon={<User size={12} />} />
                  <InfoRow label={t('employees.maritalStatus')} value={e.maritalStatus} icon={<Heart size={12} />} />
                  <InfoRow label={t('employees.bloodType')} value={e.bloodType} icon={<HeartPulse size={12} />} />
                  <InfoRow label={t('employees.militaryStatus')} value={e.militaryStatus} icon={<Shield size={12} />} />
                  <InfoRow label={t('employees.educationLevel')} value={e.lastEducationLevel} icon={<GraduationCap size={12} />} />
                  <InfoRow label={t('employees.profession')} value={e.profession} icon={<Briefcase size={12} />} />
                </div>
                <div className="border-t border-slate-100 pt-6">
                  <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4 flex items-center gap-2"><Users size={14} /> {t('employees.familyInfo')}</h4>
                  <div className="grid grid-cols-2 gap-6">
                    <InfoRow label={t('employees.fatherName')} value={e.fatherName} />
                    <InfoRow label={t('employees.motherName')} value={e.motherName} />
                    <InfoRow label={t('employees.spouseName')} value={e.spouseName} />
                    <InfoRow label={t('employees.childCount')} value={e.childCount} />
                  </div>
                </div>
              </>
            )}

            {/* ILETISIM TAB */}
            {activeTab === 'iletisim' && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 group">
                    <div className="p-2.5 bg-slate-50 text-slate-400 rounded-xl group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors"><Mail size={18} /></div>
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('employees.workEmail')}</label>
                      <a href={`mailto:${employee.email}`} className="block text-sm font-bold text-slate-800 hover:text-blue-600 transition-colors">{employee.email || t('employees.notEntered')}</a>
                    </div>
                  </div>
                  {e.personalEmail && (
                    <div className="flex items-center gap-4 group">
                      <div className="p-2.5 bg-slate-50 text-slate-400 rounded-xl group-hover:bg-purple-50 group-hover:text-purple-600 transition-colors"><Mail size={18} /></div>
                      <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('employees.personalEmail')}</label>
                        <a href={`mailto:${e.personalEmail}`} className="block text-sm font-bold text-slate-800 hover:text-purple-600 transition-colors">{e.personalEmail}</a>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-4 group">
                    <div className="p-2.5 bg-slate-50 text-slate-400 rounded-xl group-hover:bg-green-50 group-hover:text-green-600 transition-colors"><Phone size={18} /></div>
                    <div>
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('employees.mobilePhone')}</label>
                      <a href={`tel:${employee.phone}`} className="block text-sm font-bold text-slate-800 hover:text-green-600 transition-colors">{employee.phone || t('employees.notEntered')}</a>
                    </div>
                  </div>
                  {e.mobilePhone2 && (
                    <div className="flex items-center gap-4 group">
                      <div className="p-2.5 bg-slate-50 text-slate-400 rounded-xl"><Phone size={18} /></div>
                      <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('employees.secondPhone')}</label>
                        <p className="text-sm font-bold text-slate-800">{e.mobilePhone2}</p>
                      </div>
                    </div>
                  )}
                  {e.homePhone && (
                    <div className="flex items-center gap-4 group">
                      <div className="p-2.5 bg-slate-50 text-slate-400 rounded-xl"><Home size={18} /></div>
                      <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('employees.homePhone')}</label>
                        <p className="text-sm font-bold text-slate-800">{e.homePhone}</p>
                      </div>
                    </div>
                  )}
                  {e.workPhone && (
                    <div className="flex items-center gap-4 group">
                      <div className="p-2.5 bg-slate-50 text-slate-400 rounded-xl"><Phone size={18} /></div>
                      <div>
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('employees.workPhone')} {e.workExtension && `(${t('employees.extension')}: ${e.workExtension})`}</label>
                        <p className="text-sm font-bold text-slate-800">{e.workPhone}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="border-t border-slate-100 pt-6">
                  <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4 flex items-center gap-2"><Home size={14} /> {t('employees.address')}</h4>
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-slate-800">{e.address || t('employees.notEntered')}</p>
                    {(e.district || e.city || e.postalCode) && (
                      <p className="text-sm text-slate-600">{[e.district, e.city, e.postalCode].filter(Boolean).join(', ')}</p>
                    )}
                  </div>
                </div>
                {(e.emergencyContact || e.emergencyPhone) && (
                  <div className="border-t border-slate-100 pt-6">
                    <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4 flex items-center gap-2"><Heart size={14} className="text-red-500" /> {t('employees.emergencyContact')}</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <InfoRow label={t('employees.contactName')} value={e.emergencyContact} />
                      <InfoRow label={t('employees.contactPhone')} value={e.emergencyPhone} />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* FINANS TAB */}
            {activeTab === 'finans' && (
              <>
                <div className="grid grid-cols-2 gap-6">
                  <InfoRow label={t('employees.socialSecurityNo')} value={e.socialSecurityNo} icon={<Shield size={12} />} />
                  <InfoRow label={t('employees.grossSalary')} value={e.salary ? `${e.salary.toLocaleString()}` : null} icon={<Banknote size={12} />} />
                  <InfoRow label={t('employees.bank')} value={e.bankName} icon={<Building2 size={12} />} />
                  <InfoRow label={t('employees.iban')} value={e.ibanNo} icon={<CreditCard size={12} />} />
                </div>
              </>
            )}

            {/* PDKS TAB */}
            {activeTab === 'pdks' && (
              <>
                <div className="grid grid-cols-2 gap-6">
                  <InfoRow label={t('employees.employeeCode')} value={e.employeeCode} icon={<Fingerprint size={12} />} />
                  <InfoRow label={t('employees.deviceUserId')} value={e.deviceUserId} icon={<Smartphone size={12} />} />
                  <InfoRow label={t('employees.cardNumber')} value={e.cardNumber} icon={<CreditCard size={12} />} />
                  <InfoRow label={t('employees.cardType')} value={e.cardType} icon={<CreditCard size={12} />} />
                  <InfoRow label={t('employees.privilegeLevel')} value={e.privilege === 14 ? t('employees.admin') : t('employees.normalUser')} icon={<Shield size={12} />} />
                </div>
                <div className="border-t border-slate-100 pt-6">
                  <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4">{t('employees.biometricRecords')}</h4>
                  <div className="flex gap-4">
                    <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 text-sm font-bold ${e.hasCard ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                      <CreditCard size={16} />
                      {t('employees.card')} {e.hasCard ? t('employees.registered') : t('employees.notRegistered')}
                    </div>
                    <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 text-sm font-bold ${e.hasFingerprint ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                      <Fingerprint size={16} />
                      {t('employees.fingerprint')} {e.hasFingerprint ? t('employees.registered') : t('employees.notRegistered')}
                    </div>
                    <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 text-sm font-bold ${e.hasFace ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                      <User size={16} />
                      {t('employees.faceRecognition')} {e.hasFace ? t('employees.registered') : t('employees.notRegistered')}
                    </div>
                  </div>
                </div>
                {(e.mrzDocumentNumber || e.mrzDateOfBirth || e.mrzDateOfExpiry) && (
                  <div className="border-t border-slate-100 pt-6">
                    <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4">eID / MRZ (BAC)</h4>
                    <div className="grid grid-cols-3 gap-6">
                      <InfoRow label={t('employees.mrzDocumentNumber', 'Document Number')} value={e.mrzDocumentNumber} icon={<CreditCard size={12} />} />
                      <InfoRow label={t('employees.mrzDateOfBirth', 'Date of Birth (MRZ)')} value={e.mrzDateOfBirth ? new Date(e.mrzDateOfBirth).toLocaleDateString() : '-'} icon={<Calendar size={12} />} />
                      <InfoRow label={t('employees.mrzDateOfExpiry', 'Date of Expiry (MRZ)')} value={e.mrzDateOfExpiry ? new Date(e.mrzDateOfExpiry).toLocaleDateString() : '-'} icon={<Calendar size={12} />} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex gap-4">
          <button onClick={onClose} className="flex-1 py-4 bg-white border border-slate-200 text-slate-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-100 transition-all shadow-sm">{t('employees.close')}</button>
          <button onClick={onEdit} className="flex-1 py-4 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-600 transition-all shadow-xl shadow-slate-200 flex items-center justify-center gap-2">
            <Edit2 size={16} /> {t('employees.edit')}
          </button>
        </div>
      </div>

      {/* Vardiya Atama Modal */}
      {showShiftModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-slate-800">{t('employees.assignShiftModal')}</h3>
                <p className="text-sm text-slate-500 mt-1">{employee.firstName} {employee.lastName}</p>
              </div>
              <button onClick={() => setShowShiftModal(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.selectShiftLabel')}</label>
                <select
                  value={selectedShiftId || ''}
                  onChange={(e) => setSelectedShiftId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('employees.noShift')}</option>
                  {shifts.filter(s => s.isActive).map(shift => (
                    <option key={shift.id} value={shift.id}>
                      {shift.name} ({shift.startTime} - {shift.endTime})
                    </option>
                  ))}
                </select>
              </div>
              {selectedShiftId && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  {(() => {
                    const shift = shifts.find(s => s.id === selectedShiftId);
                    if (!shift) return null;
                    return (
                      <div>
                        <p className="font-bold text-slate-800">{shift.name}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {shift.startTime} - {shift.endTime}
                          {shift.workHours && <span className="text-slate-400 ml-2">({shift.workHours} {t('employees.hours')})</span>}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => setShowShiftModal(false)}
                className="flex-1 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-100 transition-all"
              >
                {t('employees.cancel')}
              </button>
              <button
                onClick={handleShiftSave}
                disabled={savingShift}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {savingShift ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}
      {showPhotoZoom && localPhotoUrl && (
        <PhotoZoomModal photoUrl={localPhotoUrl} name={`${employee.firstName} ${employee.lastName}`} onClose={() => setShowPhotoZoom(false)} />
      )}
    </div>
  );
};

const FormModal: React.FC<FormModalProps> = ({
  title, onSubmit, onClose, isEdit = false, formData, onInputChange, onPhoneChange,
  departments, positions, shifts, formError, saving, sites, locationsForForm
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<FormTab>('temel');
  const emailValid = isValidEmail(formData.email);
  const tcValid = isValidTCKimlik(formData.idNumber);
  const phoneRaw = unformatPhoneNumber(formData.phone);
  const phoneValid = !phoneRaw || phoneRaw.length === 10;

  const tabs: { id: FormTab; label: string; icon: React.ReactNode }[] = [
    { id: 'temel', label: t('employees.basic'), icon: <User size={14} /> },
    { id: 'kisisel', label: t('employees.personal'), icon: <UserCircle size={14} /> },
    { id: 'iletisim', label: t('employees.contactTab'), icon: <Phone size={14} /> },
    { id: 'aile', label: t('employees.family'), icon: <Users size={14} /> },
    { id: 'finans', label: t('employees.finance'), icon: <Banknote size={14} /> },
    { id: 'pdks', label: 'PDKS', icon: <Fingerprint size={14} /> },
  ];

  return (
  <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
    <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
      <div className="bg-white border-b border-slate-100 px-8 py-5 flex justify-between items-center">
        <h2 className="text-xl font-black text-slate-800">{title}</h2>
        <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X size={20} /></button>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-100 px-8 bg-slate-50/50">
        <div className="flex gap-1 -mb-px">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 bg-white'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8 space-y-6">
        {formError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{formError}</div>}

        {/* TEMEL BILGILER */}
        {activeTab === 'temel' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.firstName')} *</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('employees.firstName')} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.lastName')} *</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('employees.lastName')} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.department')} *</label>
                <select name="departmentId" value={formData.departmentId} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value={0}>{t('employees.selectDepartment')}</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.position')}</label>
                <select name="positionId" value={formData.positionId} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value={0}>{t('employees.selectPosition')}</option>
                  {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            {/* Site + Location dropdown — Ağustos 2026'da eklendi (çok şantiyeli müşteriler için).
                Site listesi Feature.sites=true iken customer'a görünür.
                Options prop üzerinden geliyor — bkz. modal caller ((sites, locationsForForm)). */}
            {(((sites?.length ?? 0) > 0) || ((locationsForForm?.length ?? 0) > 0)) && (
              <div className="grid grid-cols-2 gap-4">
                {((sites?.length ?? 0) > 0) && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Şantiye / Şube</label>
                    <select name="siteId" value={(formData as any).siteId ?? ''} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Bağlı değil</option>
                      {sites!.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                {((locationsForForm?.length ?? 0) > 0) && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Lokasyon</label>
                    <select name="locationId" value={(formData as any).locationId ?? ''} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçilmedi</option>
                      {locationsForForm!.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}
            {/* Mobil Sifre — Self Clock-In APK (Mavi Ajans vb.) icin. Bos birakilirsa degistirilmez. */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                Mobil Şifre <span className="text-slate-400 font-normal normal-case tracking-normal">(APK'dan giriş için, boş bırakırsanız değişmez)</span>
              </label>
              <input
                type="text"
                name="password"
                value={(formData as any).password ?? ''}
                onChange={onInputChange}
                autoComplete="off"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="6+ karakter"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('nav.shifts')}</label>
                <select name="shiftId" value={formData.shiftId} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('employees.selectShift')}</option>
                  {shifts.filter(s => s.isActive).map(s => <option key={s.id} value={s.id}>{s.name} ({s.startTime?.toString().substring(0,5)} - {s.endTime?.toString().substring(0,5)})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.annualLeaveDays')}</label>
                <input type="number" name="annualLeaveDays" value={formData.annualLeaveDays} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="14" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">İşe Giriş Tarihi</label>
                <input type="date" name="hireDate" value={formData.hireDate} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">İşten Çıkış Tarihi</label>
                <input
                  type="date"
                  name="terminationDate"
                  value={(formData as any).terminationDate || ''}
                  onChange={onInputChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ayrılmadıysa boş"
                />
                <p className="text-[10px] text-slate-400 mt-1">Ayrılmadıysa boş bırakın. Doldurulursa personel işten ayrılanlar listesinde görünür.</p>
              </div>
            </div>
            {isEdit && (
              <div className="flex items-center gap-3 pt-4">
                <input type="checkbox" name="isActive" id="isActive" checked={formData.isActive} onChange={onInputChange} className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <label htmlFor="isActive" className="text-sm font-medium text-slate-700">Aktif Personel</label>
              </div>
            )}

            {/* Yetkili Personel (Proxy Clock-In) */}
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="canProxyClockIn"
                  name="canProxyClockIn"
                  checked={!!(formData as any).canProxyClockIn}
                  onChange={onInputChange}
                  className="mt-1 w-5 h-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                />
                <div className="flex-1">
                  <label htmlFor="canProxyClockIn" className="text-sm font-bold text-amber-800 cursor-pointer">
                    Yetkili Personel (Başkası için giriş yapabilir)
                  </label>
                  <p className="text-xs text-amber-700 mt-1">
                    Saha şefi/amiri olan personel — kendi mobil uygulamasından başka personellerin NFC kartını okutarak veya yüzünü tanıtarak giriş/çıkış kaydı atabilir. Yaşlı/akıllı telefonu olmayan personeller için tasarlanmıştır. Her proxy kayıt admin panelde "Kayıt Eden" sütunuyla görünür.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* KISISEL BILGILER */}
        {activeTab === 'kisisel' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  {t('employees.idNumber')}
                  {formData.idNumber && (tcValid ? <CheckCircle size={14} className="text-green-500" /> : <XCircle size={14} className="text-red-500" />)}
                </label>
                <input
                  type="text"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    onInputChange({ ...e, target: { ...e.target, name: 'idNumber', value } } as any);
                  }}
                  className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                    formData.idNumber && !tcValid ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-blue-500'
                  }`}
                  placeholder="xxxxxxxxxxx"
                  maxLength={11}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.birthDate')}</label>
                <input type="date" name="birthDate" value={formData.birthDate} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.gender')}</label>
                <select name="gender" value={formData.gender} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('employees.select')}</option>
                  <option value="E">{t('employees.male')}</option>
                  <option value="K">{t('employees.female')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.maritalStatus')}</label>
                <select name="maritalStatus" value={formData.maritalStatus} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('employees.select')}</option>
                  <option value="Bekar">{t('employees.single')}</option>
                  <option value="Evli">{t('employees.married')}</option>
                  <option value="Bosanmis">{t('employees.divorced')}</option>
                  <option value="Dul">{t('employees.widowed')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.bloodType')}</label>
                <select name="bloodType" value={formData.bloodType} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('employees.select')}</option>
                  <option value="A+">A Rh+</option>
                  <option value="A-">A Rh-</option>
                  <option value="B+">B Rh+</option>
                  <option value="B-">B Rh-</option>
                  <option value="AB+">AB Rh+</option>
                  <option value="AB-">AB Rh-</option>
                  <option value="0+">0 Rh+</option>
                  <option value="0-">0 Rh-</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.militaryStatus')}</label>
                <select name="militaryStatus" value={formData.militaryStatus} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('employees.select')}</option>
                  <option value="Yapildi">{t('employees.completed')}</option>
                  <option value="Muaf">{t('employees.exempt')}</option>
                  <option value="Tecilli">{t('employees.deferred')}</option>
                  <option value="Yapilmadi">{t('employees.notCompleted')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.educationLevel')}</label>
                <select name="lastEducationLevel" value={formData.lastEducationLevel} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('employees.select')}</option>
                  <option value="Ilkokul">{t('employees.primarySchool')}</option>
                  <option value="Ortaokul">{t('employees.middleSchool')}</option>
                  <option value="Lise">{t('employees.highSchool')}</option>
                  <option value="OnLisans">{t('employees.associateDegree')}</option>
                  <option value="Lisans">{t('employees.bachelorDegree')}</option>
                  <option value="YuksekLisans">{t('employees.masterDegree')}</option>
                  <option value="Doktora">{t('employees.doctorate')}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.profession')}</label>
              <input type="text" name="profession" value={formData.profession} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('employees.profession')} />
            </div>
          </>
        )}

        {/* ILETISIM BILGILERI */}
        {activeTab === 'iletisim' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  {t('employees.mobilePhone')}
                  {formData.phone && (phoneValid ? <CheckCircle size={14} className="text-green-500" /> : <XCircle size={14} className="text-red-500" />)}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => onPhoneChange(e.target.value)}
                  className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                    formData.phone && !phoneValid ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-blue-500'
                  }`}
                  placeholder="(5xx) xxx xx xx"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.secondPhone')}</label>
                <input type="tel" name="mobilePhone2" value={formData.mobilePhone2} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="(5xx) xxx xx xx" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.homePhone')}</label>
                <input type="tel" name="homePhone" value={formData.homePhone} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="(0xxx) xxx xx xx" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.workPhone')} / {t('employees.extension')}</label>
                <div className="flex gap-2">
                  <input type="tel" name="workPhone" value={formData.workPhone} onChange={onInputChange} className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="(0xxx) xxx xx xx" />
                  <input type="text" name="workExtension" value={formData.workExtension} onChange={onInputChange} className="w-24 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('employees.extension')} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  {t('employees.workEmail')}
                  {formData.email && (emailValid ? <CheckCircle size={14} className="text-green-500" /> : <XCircle size={14} className="text-red-500" />)}
                </label>
                <input type="email" name="email" value={formData.email} onChange={onInputChange} className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${formData.email && !emailValid ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-blue-500'}`} placeholder="ornek@sirket.com" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.personalEmail')}</label>
                <input type="email" name="personalEmail" value={formData.personalEmail} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="ornek@gmail.com" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.address')}</label>
              <textarea name="address" value={formData.address} onChange={onInputChange} rows={2} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" placeholder={t('employees.address')} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.city')}</label>
                <input type="text" name="city" value={formData.city} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Istanbul" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.district')}</label>
                <input type="text" name="district" value={formData.district} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Kadikoy" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.postalCode')}</label>
                <input type="text" name="postalCode" value={formData.postalCode} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="34000" />
              </div>
            </div>
            <div className="border-t border-slate-100 pt-6">
              <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-4 flex items-center gap-2"><Heart size={14} className="text-red-500" /> {t('employees.emergencyContact')}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.contactName')}</label>
                  <input type="text" name="emergencyContact" value={formData.emergencyContact} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('employees.contactName')} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.contactPhone')}</label>
                  <input type="tel" name="emergencyPhone" value={formData.emergencyPhone} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="(5xx) xxx xx xx" />
                </div>
              </div>
            </div>
          </>
        )}

        {/* AILE BILGILERI */}
        {activeTab === 'aile' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.fatherName')}</label>
                <input type="text" name="fatherName" value={formData.fatherName} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('employees.fatherName')} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.motherName')}</label>
                <input type="text" name="motherName" value={formData.motherName} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('employees.motherName')} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.spouseName')}</label>
                <input type="text" name="spouseName" value={formData.spouseName} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('employees.spouseName')} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.childCount')}</label>
                <input type="number" name="childCount" value={formData.childCount} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" min="0" />
              </div>
            </div>
          </>
        )}

        {/* FINANS BILGILERI */}
        {activeTab === 'finans' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.socialSecurityNo')}</label>
                <input type="text" name="socialSecurityNo" value={formData.socialSecurityNo} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('employees.socialSecurityNo')} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.grossSalary')}</label>
                <input type="number" name="salary" value={formData.salary} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0.00" step="0.01" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.bank')}</label>
                <select name="bankName" value={formData.bankName} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('employees.select')}</option>
                  <option value="Ziraat Bankasi">Ziraat Bankasi</option>
                  <option value="Is Bankasi">Is Bankasi</option>
                  <option value="Garanti BBVA">Garanti BBVA</option>
                  <option value="Yapi Kredi">Yapi Kredi</option>
                  <option value="Akbank">Akbank</option>
                  <option value="Halkbank">Halkbank</option>
                  <option value="Vakifbank">Vakifbank</option>
                  <option value="QNB Finansbank">QNB Finansbank</option>
                  <option value="Denizbank">Denizbank</option>
                  <option value="TEB">TEB</option>
                  <option value="ING">ING</option>
                  <option value="HSBC">HSBC</option>
                  <option value="Diger">Diger</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.iban')}</label>
                <input type="text" name="ibanNo" value={formData.ibanNo} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="TR00 0000 0000 0000 0000 0000 00" />
              </div>
            </div>
          </>
        )}

        {/* PDKS CIHAZ BILGILERI */}
        {activeTab === 'pdks' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.employeeCode')}</label>
                <input type="text" name="employeeCode" value={formData.employeeCode} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="1205" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.deviceUserId')}</label>
                <input type="text" name="deviceUserId" value={formData.deviceUserId} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="1205" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Kart Numarası</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={onInputChange}
                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="11297579"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      uyar('Kartı Oku özelliği: Bir cihaza kart okutun, arka planda sunucu event alacak ve otomatik doldurulacak. Şimdilik manuel giriniz.');
                    }}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1 whitespace-nowrap"
                    title="Aktif Hikvision cihazına kart okutup otomatik doldur"
                  >
                    ⇆ Kartı Oku
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Fiziksel kart (ZKTeco / Hikvision)</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Cihaz Şifresi</label>
                <input
                  type="text"
                  name="devicePassword"
                  value={formData.devicePassword}
                  onChange={onInputChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Şifre"
                />
              </div>
            </div>

            {/* NFC UID */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">NFC UID</label>
                <input
                  type="text"
                  name="nfcUid"
                  value={(formData as any).nfcUid || ''}
                  onChange={onInputChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="A7E51992 (otomatik)"
                />
                <p className="text-[10px] text-slate-400 mt-1">Mobil NFC ilk okutmada otomatik bağlanır</p>
              </div>
            </div>

            {/* MOBIL UYGULAMA GIRISI */}
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-2 mb-3">
                <span className="text-lg">📱</span>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-amber-900">Mobil Uygulama Girişi</h4>
                  <p className="text-[10px] text-amber-700 mt-0.5">Personel NexVera Mesai APK'ya bu şifre ile giriş yapar (kart okutucu yerine)</p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-amber-800 uppercase tracking-wider mb-2">Mobil Giriş Şifresi</label>
                <input
                  type="text"
                  name="password"
                  value={(formData as any).password || ''}
                  onChange={onInputChange}
                  className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  placeholder="•••• (mevcut — değiştirmek için yeni değer girin)"
                />
                <p className="text-[11px] text-amber-800 mt-2">
                  Personel APK'da <strong>Personel Kodu + bu şifre</strong> ile giriş yapar. Boş bırakırsanız mevcut şifre korunur.
                </p>
                <p className="text-[11px] text-amber-700 mt-1">
                  <strong>İpucu:</strong> İlk kurulumda tüm personele <code className="px-1 py-0.5 bg-amber-100 rounded">1234</code> gibi geçici şifre atayabilirsiniz.
                </p>
              </div>

              {/* Baglı Cihazlar */}
              <div className="mt-4 pt-4 border-t border-amber-200">
                <div className="flex items-start gap-2 mb-2">
                  <span className="text-lg">📱</span>
                  <h4 className="text-sm font-bold text-amber-900">Bağlı Mobil Cihazlar</h4>
                </div>
                <p className="text-[11px] text-amber-700">
                  Bu personel henüz hiçbir mobil cihaza kayıtlı değil. İlk APK girişinde otomatik bağlanır.
                </p>
                <p className="text-[10px] text-amber-600 mt-2">
                  <strong>Sıfırlama ne zaman gerekir?</strong> Personel işten çıktı yerine yeni personel geldi ve aynı telefonu kullanacak — eski personelin bind'ini sıfırla, yeni personel giriş yapabilir.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.cardType')}</label>
                <select name="cardType" value={formData.cardType} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('employees.select')}</option>
                  <option value="Kart">Kart</option>
                  <option value="Mifare">Mifare</option>
                  <option value="ID">ID Kart</option>
                  <option value="Proximity">Proximity</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.privilegeLevel')}</label>
                <select name="privilege" value={formData.privilege} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value={0}>{t('employees.normalUser')}</option>
                  <option value={14}>{t('employees.admin')}</option>
                </select>
              </div>
            </div>
            <div className="flex gap-6 pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="hasCard" checked={formData.hasCard} onChange={onInputChange} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-slate-700">{t('employees.cardRegistered')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="hasFingerprint" checked={formData.hasFingerprint} onChange={onInputChange} className="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500" />
                <span className="text-sm text-slate-700">{t('employees.fingerprintRegistered')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="hasFace" checked={formData.hasFace} onChange={onInputChange} className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500" />
                <span className="text-sm text-slate-700">{t('employees.faceRegistered')}</span>
              </label>
            </div>

            {/* eID / MRZ (BAC Authentication) */}
            <div className="pt-6 border-t border-slate-200 mt-4">
              <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-4">eID / MRZ (BAC Authentication)</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.mrzDocumentNumber', 'Document Number')}</label>
                  <input type="text" name="mrzDocumentNumber" value={formData.mrzDocumentNumber} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="A01B23456" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.mrzDateOfBirth', 'Date of Birth (MRZ)')}</label>
                  <input type="date" name="mrzDateOfBirth" value={formData.mrzDateOfBirth} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('employees.mrzDateOfExpiry', 'Date of Expiry (MRZ)')}</label>
                  <input type="date" name="mrzDateOfExpiry" value={formData.mrzDateOfExpiry} onChange={onInputChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">Required for eID/national ID card NFC authentication (ICAO 9303 BAC)</p>
            </div>
          </>
        )}
      </div>

      <div className="bg-slate-50 border-t border-slate-100 px-8 py-5 flex gap-4">
        <button onClick={onClose} className="flex-1 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-100 transition-all">{t('employees.cancel')}</button>
        <button onClick={onSubmit} disabled={saving} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
          {saving ? t('employees.saving') : t('employees.save')}
        </button>
      </div>
    </div>
  </div>
  );
};

const emptyFormData: EmployeeFormData = {
  // Temel Bilgiler
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  departmentId: 0,
  positionId: 0,
  idNumber: '',
  hireDate: '',
  terminationDate: '',
  birthDate: '',
  gender: '',
  address: '',
  isActive: true,

  // Kisisel Bilgiler
  maritalStatus: '',
  bloodType: '',
  militaryStatus: '',
  lastEducationLevel: '',
  profession: '',

  // Iletisim Bilgileri Extended
  homePhone: '',
  mobilePhone2: '',
  workPhone: '',
  workExtension: '',
  personalEmail: '',
  city: '',
  district: '',
  postalCode: '',

  // Acil Durum
  emergencyContact: '',
  emergencyPhone: '',

  // Aile Bilgileri
  spouseName: '',
  fatherName: '',
  motherName: '',
  childCount: '',

  // Finans & SGK
  socialSecurityNo: '',
  salary: '',
  bankName: '',
  ibanNo: '',

  // Izin Bilgileri
  annualLeaveDays: '',

  // Vardiya
  shiftId: '',

  // PDKS Device fields
  employeeCode: '',
  cardNumber: '',
  nfcUid: '',
  deviceUserId: '',
  devicePassword: '',
  hasFingerprint: false,
  hasCard: false,
  hasFace: false,
  cardType: '',
  privilege: 0,

  // eID / MRZ (BAC authentication)
  mrzDocumentNumber: '',
  mrzDateOfBirth: '',
  mrzDateOfExpiry: '',

  // Site + Location + Mobil Sifre (Ag 2026)
  siteId: '',
  locationId: '',
  password: '',
};

const Employees = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [selectedDept, setSelectedDept] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  // Site + Location listeleri (FormModal ile filter icin ortak)
  const [sitesList, setSitesList] = useState<{ id: number; name: string }[]>([]);
  const [locationsList, setLocationsList] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const s = await siteService.getDropdown();
        if (s.success && s.data) setSitesList(s.data.map(x => ({ id: x.id, name: x.name })));
      } catch { }
      try {
        const l = await locationService.getForDropdown();
        if (l.success && l.data) setLocationsList(l.data.map(x => ({ id: x.id, name: x.name })));
      } catch { }
    })();
  }, []);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showToggleStatusModal, setShowToggleStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState<{ deleted: number; failed: { id: number; reason: string }[] } | null>(null);
  const [formData, setFormData] = useState<EmployeeFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [empRes, deptRes, posRes, shiftRes] = await Promise.all([
        employeeService.getAll(undefined),
        departmentService.getAll(),
        positionService.getAll(),
        shiftService.getAll()
      ]);

      if (empRes.success) setEmployees(empRes.data);
      if (deptRes.success) setDepartments(deptRes.data);
      if (posRes.success) setPositions(posRes.data);
      if (shiftRes.success) setShifts(shiftRes.data);

    } catch (err: any) {
      console.error('Load error:', err);
      setError(err.response?.data?.message || t('employees.errorLoadingData'));
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentName = (id: number) =>
    departments.find(d => d.id === id)?.name || t('employees.department');

  // Photo upload handler
  const handlePhotoUpload = async (employeeId: number, photoBase64: string | null) => {
    try {
      await employeeService.uploadPhoto(employeeId, photoBase64);
      // Update the employee in local state
      setEmployees(prev => prev.map(emp =>
        emp.id === employeeId ? { ...emp, photoUrl: photoBase64 || undefined } : emp
      ));
      // Also update selectedEmployee if it's the same
      if (selectedEmployee?.id === employeeId) {
        setSelectedEmployee(prev => prev ? { ...prev, photoUrl: photoBase64 || undefined } : null);
      }
    } catch (err: any) {
      console.error('Photo upload error:', err);
      throw err;
    }
  };

  // Shift assignment handler
  const handleShiftAssign = async (employeeId: number, shiftId: number | null) => {
    try {
      if (shiftId) {
        await shiftService.assignShift({
          employeeId,
          shiftId,
          startDate: new Date().toISOString().split('T')[0]
        });
      } else {
        await shiftService.removeEmployeeShift(employeeId);
      }
    } catch (err: any) {
      console.error('Shift assign error:', err);
      throw err;
    }
  };

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp: any) => {
      const searchScope = `${emp.firstName} ${emp.lastName} ${emp.positionName || ''} ${emp.idNumber || ''} ${emp.employeeCode || ''} ${emp.cardNumber || ''}`.toLowerCase();
      const matchesSearch = searchScope.includes(searchTerm.toLowerCase());
      const matchesDept = selectedDept === 'all' || emp.departmentId === parseInt(selectedDept);
      const hasTermination = !!(emp.terminationDate || emp.TerminationDate);
      const matchesStatus = selectedStatus === 'all' ||
        (selectedStatus === 'active' && emp.isActive && !hasTermination) ||
        (selectedStatus === 'inactive' && !emp.isActive && !hasTermination) ||
        (selectedStatus === 'terminated' && hasTermination);
      // Site/Location filter — personel dogrudan siteId'ye sahip veya departmani araciligi ile
      const empSite = emp.siteId ?? emp.siteID ?? emp.departmentSiteId ?? null;
      const empLoc  = emp.locationId ?? emp.locationID ?? emp.departmentLocationId ?? null;
      const matchesSite = selectedSite === 'all' || String(empSite) === selectedSite;
      const matchesLoc  = selectedLocation === 'all' || String(empLoc) === selectedLocation;

      return matchesSearch && matchesDept && matchesStatus && matchesSite && matchesLoc;
    });
  }, [employees, searchTerm, selectedDept, selectedStatus, selectedSite, selectedLocation]);

  // CRUD handlers
  const handleCreate = () => {
    setFormData(emptyFormData);
    setFormError(null);
    setShowCreateModal(true);
  };

  const handleEdit = async (emp: Employee) => {
    const e = emp as any;

    // Personelin mevcut vardiyasini yukle
    let currentShiftId = '';
    try {
      const shiftResult = await shiftService.getEmployeeShift(emp.id);
      if (shiftResult.success && shiftResult.data) {
        const d = shiftResult.data as any;
        const sid = d.shiftID || d.ShiftID || d.shiftId;
        if (sid) currentShiftId = sid.toString();
      }
    } catch (err) {
      console.error('Vardiya bilgisi yuklenemedi:', err);
    }

    setFormData({
      // Temel Bilgiler
      firstName: emp.firstName,
      lastName: emp.lastName,
      email: emp.email || '',
      phone: emp.phone ? formatPhoneNumber(emp.phone) : '',
      departmentId: emp.departmentId,
      positionId: emp.positionId || 0,
      idNumber: emp.idNumber || '',
      hireDate: emp.hireDate ? emp.hireDate.split('T')[0] : '',
      terminationDate: (emp as any).terminationDate ? (emp as any).terminationDate.split('T')[0] : '',
      birthDate: emp.birthDate ? emp.birthDate.split('T')[0] : '',
      gender: emp.gender || '',
      address: emp.address || '',
      isActive: emp.isActive,

      // Kisisel Bilgiler
      maritalStatus: e.maritalStatus || '',
      bloodType: e.bloodType || '',
      militaryStatus: e.militaryStatus || '',
      lastEducationLevel: e.lastEducationLevel || '',
      profession: e.profession || '',

      // Iletisim Bilgileri Extended
      homePhone: e.homePhone || '',
      mobilePhone2: e.mobilePhone2 || '',
      workPhone: e.workPhone || '',
      workExtension: e.workExtension || '',
      personalEmail: e.personalEmail || '',
      city: e.city || '',
      district: e.district || '',
      postalCode: e.postalCode || '',

      // Acil Durum
      emergencyContact: e.emergencyContact || '',
      emergencyPhone: e.emergencyPhone || '',

      // Aile Bilgileri
      spouseName: e.spouseName || '',
      fatherName: e.fatherName || '',
      motherName: e.motherName || '',
      childCount: e.childCount?.toString() || '',

      // Finans & SGK
      socialSecurityNo: e.socialSecurityNo || '',
      salary: e.salary?.toString() || '',
      bankName: e.bankName || '',
      ibanNo: e.ibanNo || '',

      // Izin Bilgileri
      annualLeaveDays: e.annualLeaveDays?.toString() || '',

      // Vardiya
      shiftId: currentShiftId,

      // PDKS Device fields
      employeeCode: e.employeeCode || '',
      cardNumber: e.cardNumber || '',
      nfcUid: e.nfcUid || e.NfcUid || '',
      deviceUserId: e.deviceUserId?.toString() || '',
      devicePassword: e.devicePassword || '',
      hasFingerprint: e.hasFingerprint || false,
      hasCard: e.hasCard || false,
      hasFace: e.hasFace || false,
      cardType: e.cardType || '',
      privilege: e.privilege || 0,

      // eID / MRZ
      mrzDocumentNumber: e.mrzDocumentNumber || '',
      mrzDateOfBirth: e.mrzDateOfBirth ? e.mrzDateOfBirth.split('T')[0] : '',
      mrzDateOfExpiry: e.mrzDateOfExpiry ? e.mrzDateOfExpiry.split('T')[0] : '',

      // Site + Location + Mobil Sifre (Ag 2026)
      siteId: e.siteId ?? e.siteID ?? '',
      locationId: e.locationId ?? e.locationID ?? '',
      password: '',  // Sifreyi editte gostermeyiz — yeni deger girilirse yazilir
    });
    setFormError(null);
    setSelectedEmployee(emp);
    setShowEditModal(true);
  };

  const handleToggleStatus = (emp: Employee) => {
    if (!hasPermission('canEditEmployees')) return;
    setSelectedEmployee(emp);
    setShowToggleStatusModal(true);
  };

  const submitCreate = async () => {
    if (!formData.firstName || !formData.lastName) {
      setFormError(t('employees.firstNameLastNameRequired'));
      return;
    }
    if (!formData.departmentId) {
      setFormError(t('employees.selectDepartmentError'));
      return;
    }
    if (formData.email && !isValidEmail(formData.email)) {
      setFormError(t('employees.invalidEmail'));
      return;
    }
    if (formData.idNumber && !isValidTCKimlik(formData.idNumber)) {
      setFormError(t('employees.invalidIdNumber'));
      return;
    }
    const phoneRaw = unformatPhoneNumber(formData.phone);
    if (phoneRaw && phoneRaw.length !== 10) {
      setFormError(t('employees.phoneMustBe10Digits'));
      return;
    }

    try {
      setSaving(true);
      setFormError(null);

      const dto: EmployeeCreateDto = {
        // Temel Bilgiler
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || undefined,
        phone: phoneRaw || undefined,
        departmentId: formData.departmentId,
        positionId: formData.positionId || undefined,
        idNumber: formData.idNumber || undefined,
        hireDate: formData.hireDate || undefined,
        birthDate: formData.birthDate || undefined,
        gender: formData.gender || undefined,
        address: formData.address || undefined,

        // Kisisel Bilgiler
        maritalStatus: formData.maritalStatus || undefined,
        bloodType: formData.bloodType || undefined,
        militaryStatus: formData.militaryStatus || undefined,
        lastEducationLevel: formData.lastEducationLevel || undefined,
        profession: formData.profession || undefined,

        // Iletisim Bilgileri Extended
        homePhone: formData.homePhone || undefined,
        mobilePhone2: formData.mobilePhone2 || undefined,
        workPhone: formData.workPhone || undefined,
        workExtension: formData.workExtension || undefined,
        personalEmail: formData.personalEmail || undefined,
        city: formData.city || undefined,
        district: formData.district || undefined,
        postalCode: formData.postalCode || undefined,

        // Acil Durum
        emergencyContact: formData.emergencyContact || undefined,
        emergencyPhone: formData.emergencyPhone || undefined,

        // Aile Bilgileri
        spouseName: formData.spouseName || undefined,
        fatherName: formData.fatherName || undefined,
        motherName: formData.motherName || undefined,
        childCount: formData.childCount ? parseInt(formData.childCount) : undefined,

        // Finans & SGK
        socialSecurityNo: formData.socialSecurityNo || undefined,
        salary: formData.salary ? parseFloat(formData.salary) : undefined,
        bankName: formData.bankName || undefined,
        ibanNo: formData.ibanNo || undefined,

        // Izin Bilgileri
        annualLeaveDays: formData.annualLeaveDays ? parseInt(formData.annualLeaveDays) : undefined,

        // PDKS Device fields
        employeeCode: formData.employeeCode || undefined,
        cardNumber: formData.cardNumber || undefined,
        nfcUid: (formData as any).nfcUid || undefined,
        deviceUserId: formData.deviceUserId ? parseInt(formData.deviceUserId) : undefined,
        devicePassword: formData.devicePassword || undefined,
        hasFingerprint: formData.hasFingerprint,
        hasCard: formData.hasCard,
        hasFace: formData.hasFace,
        cardType: formData.cardType || undefined,
        privilege: formData.privilege,

        // eID / MRZ
        mrzDocumentNumber: formData.mrzDocumentNumber || undefined,
        mrzDateOfBirth: formData.mrzDateOfBirth || undefined,
        mrzDateOfExpiry: formData.mrzDateOfExpiry || undefined,

        // Site + Location + Mobil Sifre (Ag 2026)
        siteId: (formData as any).siteId ? parseInt(String((formData as any).siteId)) : undefined,
        locationId: (formData as any).locationId ? parseInt(String((formData as any).locationId)) : undefined,
        password: (formData as any).password || undefined,
      } as any;

      const res = await employeeService.create(dto);
      if (res.success) {
        // Yeni personele vardiya ata
        if (formData.shiftId && res.data?.id) {
          try {
            await handleShiftAssign(res.data.id, parseInt(formData.shiftId));
          } catch (shiftErr) {
            console.error('Vardiya atama hatasi:', shiftErr);
          }
        }
        setShowCreateModal(false);
        await loadData();
      } else {
        setFormError(res.message || t('employees.couldNotAdd'));
      }
    } catch (err: any) {
      setFormError(err.response?.data?.message || t('employees.errorAdding'));
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!selectedEmployee) return;
    if (!formData.firstName || !formData.lastName) {
      setFormError(t('employees.firstNameLastNameRequired'));
      return;
    }
    if (formData.email && !isValidEmail(formData.email)) {
      setFormError(t('employees.invalidEmail'));
      return;
    }
    if (formData.idNumber && !isValidTCKimlik(formData.idNumber)) {
      setFormError(t('employees.invalidIdNumber'));
      return;
    }
    const phoneRaw = unformatPhoneNumber(formData.phone);
    if (phoneRaw && phoneRaw.length !== 10) {
      setFormError(t('employees.phoneMustBe10Digits'));
      return;
    }

    try {
      setSaving(true);
      setFormError(null);

      const dto: EmployeeUpdateDto = {
        // Temel Bilgiler
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || undefined,
        phone: phoneRaw || undefined,
        departmentId: formData.departmentId || undefined,
        positionId: formData.positionId || undefined,
        idNumber: formData.idNumber || undefined,
        hireDate: formData.hireDate || undefined,
        terminationDate: (formData as any).terminationDate || null,
        birthDate: formData.birthDate || undefined,
        gender: formData.gender || undefined,
        address: formData.address || undefined,
        isActive: formData.isActive,

        // Kisisel Bilgiler
        maritalStatus: formData.maritalStatus || undefined,
        bloodType: formData.bloodType || undefined,
        militaryStatus: formData.militaryStatus || undefined,
        lastEducationLevel: formData.lastEducationLevel || undefined,
        profession: formData.profession || undefined,

        // Iletisim Bilgileri Extended
        homePhone: formData.homePhone || undefined,
        mobilePhone2: formData.mobilePhone2 || undefined,
        workPhone: formData.workPhone || undefined,
        workExtension: formData.workExtension || undefined,
        personalEmail: formData.personalEmail || undefined,
        city: formData.city || undefined,
        district: formData.district || undefined,
        postalCode: formData.postalCode || undefined,

        // Acil Durum
        emergencyContact: formData.emergencyContact || undefined,
        emergencyPhone: formData.emergencyPhone || undefined,

        // Aile Bilgileri
        spouseName: formData.spouseName || undefined,
        fatherName: formData.fatherName || undefined,
        motherName: formData.motherName || undefined,
        childCount: formData.childCount ? parseInt(formData.childCount) : undefined,

        // Finans & SGK
        socialSecurityNo: formData.socialSecurityNo || undefined,
        salary: formData.salary ? parseFloat(formData.salary) : undefined,
        bankName: formData.bankName || undefined,
        ibanNo: formData.ibanNo || undefined,

        // Izin Bilgileri
        annualLeaveDays: formData.annualLeaveDays ? parseInt(formData.annualLeaveDays) : undefined,

        // PDKS Device fields
        employeeCode: formData.employeeCode || undefined,
        cardNumber: formData.cardNumber || undefined,
        nfcUid: (formData as any).nfcUid || undefined,
        deviceUserId: formData.deviceUserId ? parseInt(formData.deviceUserId) : undefined,
        devicePassword: formData.devicePassword || undefined,
        hasFingerprint: formData.hasFingerprint,
        hasCard: formData.hasCard,
        hasFace: formData.hasFace,
        cardType: formData.cardType || undefined,
        privilege: formData.privilege,

        // eID / MRZ
        mrzDocumentNumber: formData.mrzDocumentNumber || undefined,
        mrzDateOfBirth: formData.mrzDateOfBirth || undefined,
        mrzDateOfExpiry: formData.mrzDateOfExpiry || undefined,

        // Site + Location + Mobil Sifre (Ag 2026)
        siteId: (formData as any).siteId ? parseInt(String((formData as any).siteId)) : null,
        locationId: (formData as any).locationId ? parseInt(String((formData as any).locationId)) : null,
        password: (formData as any).password || undefined,
      } as any;

      const res = await employeeService.update(selectedEmployee.id, dto);
      if (res.success) {
        // Vardiya atamasini guncelle
        try {
          const newShiftId = formData.shiftId ? parseInt(formData.shiftId) : null;
          await handleShiftAssign(selectedEmployee.id, newShiftId);
        } catch (shiftErr) {
          console.error('Vardiya atama hatasi:', shiftErr);
        }

        setShowEditModal(false);
        setSelectedEmployee(null);
        await loadData();
      } else {
        setFormError(res.message || t('employees.updateFailed'));
      }
    } catch (err: any) {
      setFormError(err.response?.data?.message || t('employees.errorUpdating'));
    } finally {
      setSaving(false);
    }
  };

  const submitToggleStatus = async () => {
    if (!selectedEmployee) return;

    try {
      setSaving(true);
      // Toggle the isActive status
      const dto: EmployeeUpdateDto = {
        isActive: !selectedEmployee.isActive,
      };
      const res = await employeeService.update(selectedEmployee.id, dto);
      if (res.success) {
        setShowToggleStatusModal(false);
        setSelectedEmployee(null);
        await loadData();
      }
    } catch (err: any) {
      console.error('Toggle status error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (emp: Employee) => {
    if (!hasPermission('canEditEmployees')) return;
    setSelectedEmployee(emp);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const submitDelete = async () => {
    if (!selectedEmployee) return;

    try {
      setDeleting(true);
      setDeleteError(null);
      const res = await employeeService.delete(selectedEmployee.id);
      if (res.success) {
        setShowDeleteModal(false);
        setSelectedEmployee(null);
        await loadData();
      } else {
        // Backend hareket kaydı varsa hata mesajı döner
        setDeleteError(res.message || t('employees.deleteFailed'));
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || t('employees.errorDeleting');
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };

  // Bulk selection functions
  const toggleSelectEmployee = (id: number) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredEmployees.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEmployees.map(e => e.id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0 || !hasPermission('canEditEmployees')) return;
    setBulkDeleteResult(null);
    setShowBulkDeleteModal(true);
  };

  const submitBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    try {
      setBulkDeleting(true);
      const res = await employeeService.bulkDelete(Array.from(selectedIds));
      if (res.success) {
        setBulkDeleteResult({
          deleted: res.data.deletedIds.length,
          failed: res.data.failedIds
        });
        setSelectedIds(new Set());
        await loadData();
      }
    } catch (err: any) {
      console.error('Bulk delete error:', err);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">{t('employees.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">{t('employees.error')}</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={loadData} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
          {t('employees.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 h-full relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">{t('employees.directory')}</h1>
          <p className="text-slate-500 text-sm font-medium">{t('employees.totalRegistered', { count: employees.length })}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-medium">
            <RefreshCw size={16} /> {t('employees.refresh')}
          </button>
          {selectedIds.size > 0 && hasPermission('canEditEmployees') && (
            <button onClick={handleBulkDelete} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
              <Trash2 size={16} /> {t('employees.deleteSelected', { count: selectedIds.size })}
            </button>
          )}
          {hasPermission('canCreateEmployees') && (
            <button onClick={handleCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              <Plus size={16} /> {t('employees.newEmployee')}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input type="text" placeholder={t('employees.searchPlaceholder')} className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 text-sm font-medium transition-all" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={selectedDept} onChange={(e) => setSelectedDept(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-600 outline-none focus:ring-4 focus:ring-blue-500/10 min-w-[180px] transition-all cursor-pointer">
            <option value="all">{t('employees.allDepartments')}</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-600 outline-none focus:ring-4 focus:ring-blue-500/10 min-w-[180px] transition-all cursor-pointer">
            <option value="all">Tüm Durumlar</option>
            <option value="active">Aktif</option>
            <option value="inactive">Pasif</option>
            <option value="terminated">İşten Ayrılan</option>
          </select>
          {/* Ag 2026: Şantiye + Lokasyon filtresi (çok şantiyeli müşteriler için).
              Feature.sites=true iken Şantiye dropdown, her zaman Lokasyon dropdown görünür. */}
          <SiteLocationFilter
            selectedSiteId={selectedSite}
            selectedLocationId={selectedLocation}
            onSiteChange={setSelectedSite}
            onLocationChange={setSelectedLocation}
          />
          <div className="flex bg-slate-50 border border-slate-200 rounded-xl p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'card' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              title={t('employees.cardView')}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              title={t('employees.listView')}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredEmployees.map(emp => (
            <div key={emp.id} className={`bg-white rounded-[2.5rem] p-6 border shadow-sm hover:shadow-xl transition-all duration-300 group relative overflow-hidden ${selectedIds.has(emp.id) ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200 hover:border-blue-300'}`}>
              {hasPermission('canEditEmployees') && (
                <div className="absolute top-4 left-4 z-10">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(emp.id)}
                      onChange={() => toggleSelectEmployee(emp.id)}
                      className="w-5 h-5 rounded-lg border-2 border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                    />
                  </label>
                </div>
              )}
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="relative">
                  <LazyPhoto employeeId={emp.id} name={`${emp.firstName} ${emp.lastName}`} />
                  <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-4 border-white shadow-lg ${emp.isActive ? 'bg-green-500' : 'bg-slate-400'}`} title={emp.isActive ? t('employees.active') : t('employees.inactive')}></div>
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-800">{emp.firstName} {emp.lastName}</h3>
                  <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mt-1">{emp.positionName || emp.positionTitle || t('employees.noPosition')}</p>
                </div>
                <div className="w-full pt-4 border-t border-slate-50 space-y-2">
                  <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                    <Briefcase size={12} className="text-slate-300" />
                    <span className="truncate">{emp.departmentName || getDepartmentName(emp.departmentId)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                    <Fingerprint size={12} className="text-slate-300" />
                    <span>ID: {emp.employeeId}</span>
                  </div>
                </div>
                <div className="w-full pt-2 flex gap-2">
                  <button onClick={() => setSelectedEmployee(emp)} className="flex-1 bg-slate-50 text-slate-400 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 border border-slate-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all">
                    <Info size={12} /> {t('employees.detail')}
                  </button>
                  <button onClick={() => navigate(`/employee-stats?id=${emp.id}`)} className="bg-slate-50 text-slate-400 py-2 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center border border-slate-100 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200 transition-all" title={t('employees.statistics')}>
                    <BarChart3 size={12} />
                  </button>
                  <button onClick={() => navigate(`/attendance-logs?employeeId=${emp.id}`)} className="bg-slate-50 text-slate-400 py-2 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center border border-slate-100 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-all" title={t('employees.entryExit')}>
                    <DoorOpen size={12} />
                  </button>
                  <button onClick={() => navigate(`/timesheet?employeeId=${emp.id}`)} className="bg-slate-50 text-slate-400 py-2 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center border border-slate-100 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition-all" title={t('employees.timesheet')}>
                    <Calculator size={12} />
                  </button>
                  {hasPermission('canEditEmployees') ? (
                    <>
                      <button onClick={() => handleEdit(emp)} className="flex-1 bg-slate-50 text-slate-400 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 border border-slate-100 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 transition-all">
                        <Edit2 size={12} /> {t('employees.edit')}
                      </button>
                      <button onClick={() => handleToggleStatus(emp)} className={`bg-slate-50 py-2 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center border border-slate-100 transition-all ${emp.isActive ? 'text-slate-400 hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200' : 'text-slate-400 hover:bg-green-50 hover:text-green-600 hover:border-green-200'}`} title={emp.isActive ? t('employees.deactivate') : t('employees.activate')}>
                        {emp.isActive ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                      </button>
                      <button onClick={() => handleDelete(emp)} className="bg-slate-50 py-2 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center border border-slate-100 transition-all text-slate-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200" title={t('employees.delete')}>
                        <Trash2 size={12} />
                      </button>
                    </>
                  ) : (
                    <div className="flex-1 bg-slate-50 text-slate-300 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5 border border-slate-100">
                      <Lock size={12} /> {t('employees.unauthorized')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {hasPermission('canEditEmployees') && (
                  <th className="w-12 px-4 py-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === filteredEmployees.length && filteredEmployees.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-2 border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                      />
                    </label>
                  </th>
                )}
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('employees.personnel')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('employees.position')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('employees.department')}</th>
                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('employees.contact')}</th>
                <th className="text-left px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('employees.cardNo')}</th>
                <th className="text-center px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('employees.status')}</th>
                <th className="text-right px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('employees.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredEmployees.map(emp => (
                <tr key={emp.id} className={`transition-colors group ${selectedIds.has(emp.id) ? 'bg-blue-50/50' : 'hover:bg-slate-50/50'}`}>
                  {hasPermission('canEditEmployees') && (
                    <td className="w-12 px-4 py-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(emp.id)}
                          onChange={() => toggleSelectEmployee(emp.id)}
                          className="w-4 h-4 rounded border-2 border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                        />
                      </label>
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <LazyPhoto employeeId={emp.id} name={`${emp.firstName} ${emp.lastName}`} size="w-10 h-10" rounded="rounded-xl" />
                      <div>
                        <div className="font-bold text-slate-800">{emp.firstName} {emp.lastName}</div>
                        <div className="text-[10px] text-slate-400 font-medium">ID: {emp.employeeId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-slate-700">{emp.positionName || emp.positionTitle || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-slate-700">{emp.departmentName || getDepartmentName(emp.departmentId)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {emp.email && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Mail size={12} className="text-slate-300" />
                          <span className="truncate max-w-[150px]">{emp.email}</span>
                        </div>
                      )}
                      {emp.phone && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Phone size={12} className="text-slate-300" />
                          <span>{emp.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    {emp.cardNumber ? (
                      <div className="flex items-center gap-2">
                        <CreditCard size={14} className="text-slate-400" />
                        <span className="text-sm font-mono text-slate-700">{emp.cardNumber}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-300">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${emp.isActive ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-slate-100 text-slate-400 border border-slate-200'}`}>
                      {emp.isActive ? t('employees.active') : t('employees.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setSelectedEmployee(emp)} className="p-2 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title={t('employees.detail')}>
                        <Info size={16} />
                      </button>
                      <button onClick={() => navigate(`/employee-stats?id=${emp.id}`)} className="p-2 text-slate-300 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all" title={t('employees.statistics')}>
                        <BarChart3 size={16} />
                      </button>
                      {hasPermission('canEditEmployees') ? (
                        <>
                          <button onClick={() => handleEdit(emp)} className="p-2 text-slate-300 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all" title={t('employees.edit')}>
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => handleToggleStatus(emp)} className={`p-2 rounded-lg transition-all ${emp.isActive ? 'text-slate-300 hover:text-orange-600 hover:bg-orange-50' : 'text-slate-300 hover:text-green-600 hover:bg-green-50'}`} title={emp.isActive ? t('employees.deactivate') : t('employees.activate')}>
                            {emp.isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                          </button>
                          <button onClick={() => handleDelete(emp)} className="p-2 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title={t('employees.delete')}>
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        <div className="p-2 text-slate-200" title={t('employees.unauthorized')}>
                          <Lock size={16} />
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedEmployee && !showEditModal && !showToggleStatusModal && (
        <EmployeeDetailPanel
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
          onEdit={() => handleEdit(selectedEmployee)}
          getDepartmentName={getDepartmentName}
          onPhotoUpload={handlePhotoUpload}
          shifts={shifts}
          onShiftAssign={handleShiftAssign}
        />
      )}

      {showCreateModal && (
        <FormModal
          title={t('employees.newEmployeeTitle')}
          onSubmit={submitCreate}
          onClose={() => setShowCreateModal(false)}
          formData={formData}
          onInputChange={handleInputChange}
          onPhoneChange={handlePhoneChange}
          departments={departments}
          positions={positions}
          shifts={shifts}
          formError={formError}
          saving={saving}
          sites={sitesList}
          locationsForForm={locationsList}
        />
      )}
      {showEditModal && (
        <FormModal
          title={t('employees.editEmployeeTitle')}
          onSubmit={submitEdit}
          onClose={() => { setShowEditModal(false); setSelectedEmployee(null); }}
          isEdit={true}
          formData={formData}
          onInputChange={handleInputChange}
          onPhoneChange={handlePhoneChange}
          departments={departments}
          positions={positions}
          shifts={shifts}
          formError={formError}
          saving={saving}
          sites={sitesList}
          locationsForForm={locationsList}
        />
      )}

      {showToggleStatusModal && selectedEmployee && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${selectedEmployee.isActive ? 'bg-orange-100' : 'bg-green-100'}`}>
                {selectedEmployee.isActive ? <ToggleRight className="w-8 h-8 text-orange-600" /> : <ToggleLeft className="w-8 h-8 text-green-600" />}
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">
                {selectedEmployee.isActive ? t('employees.deactivateEmployee') : t('employees.activateEmployee')}
              </h3>
              <p className="text-slate-500 mb-6" dangerouslySetInnerHTML={{ __html: selectedEmployee.isActive ? t('employees.confirmDeactivate', { name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}` }) : t('employees.confirmActivate', { name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}` }) }} />
              <div className="flex gap-4">
                <button onClick={() => { setShowToggleStatusModal(false); setSelectedEmployee(null); }} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all">{t('employees.cancel')}</button>
                <button onClick={submitToggleStatus} disabled={saving} className={`flex-1 py-3 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${selectedEmployee.isActive ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`}>
                  {saving ? <Loader2 size={18} className="animate-spin" /> : (selectedEmployee.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />)}
                  {saving ? t('employees.saving') : (selectedEmployee.isActive ? t('employees.deactivate') : t('employees.activate'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedEmployee && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-100">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">
                {t('employees.deleteEmployee')}
              </h3>
              <p className="text-slate-500 mb-4" dangerouslySetInnerHTML={{ __html: t('employees.confirmDeleteEmployee', { name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}` }) }} />

              {deleteError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-left">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-800 text-sm font-medium">{deleteError}</p>
                      {(deleteError.includes('hareket') || deleteError.includes('record') || deleteError.includes('attendance')) && (
                        <p className="text-red-600 text-xs mt-2">
                          {t('employees.hasAttendanceRecords')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={() => { setShowDeleteModal(false); setSelectedEmployee(null); setDeleteError(null); }}
                  className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all"
                >
                  {t('employees.cancel')}
                </button>
                {!(deleteError?.includes('hareket') || deleteError?.includes('record') || deleteError?.includes('attendance')) ? (
                  <button
                    onClick={submitDelete}
                    disabled={deleting}
                    className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {deleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                    {deleting ? t('employees.deleting') : t('employees.delete')}
                  </button>
                ) : (
                  <button
                    onClick={() => { setShowDeleteModal(false); handleToggleStatus(selectedEmployee); }}
                    className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                  >
                    <ToggleRight size={18} />
                    {t('employees.deactivate')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-[70] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-100">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">
                {t('employees.bulkDelete')}
              </h3>

              {!bulkDeleteResult ? (
                <>
                  <p className="text-slate-500 mb-6" dangerouslySetInnerHTML={{ __html: t('employees.confirmBulkDelete', { count: selectedIds.size }) }} />
                  <div className="flex gap-4">
                    <button
                      onClick={() => { setShowBulkDeleteModal(false); }}
                      className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all"
                    >
                      {t('employees.cancel')}
                    </button>
                    <button
                      onClick={submitBulkDelete}
                      disabled={bulkDeleting}
                      className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {bulkDeleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                      {bulkDeleting ? t('employees.deleting') : t('employees.delete')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-6 space-y-3">
                    {bulkDeleteResult.deleted > 0 && (
                      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-800 font-medium">{t('employees.successfullyDeleted', { count: bulkDeleteResult.deleted })}</span>
                      </div>
                    )}
                    {bulkDeleteResult.failed.length > 0 && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-center gap-3 mb-2">
                          <XCircle className="w-5 h-5 text-red-600" />
                          <span className="text-red-800 font-medium">{t('employees.couldNotDelete', { count: bulkDeleteResult.failed.length })}</span>
                        </div>
                        <div className="text-left max-h-32 overflow-y-auto">
                          {bulkDeleteResult.failed.map((f, i) => (
                            <p key={i} className="text-red-600 text-xs mt-1">ID {f.id}: {f.reason}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowBulkDeleteModal(false);
                      setBulkDeleteResult(null);
                      setSelectedIds(new Set());
                    }}
                    className="w-full py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all"
                  >
                    {t('employees.close')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {filteredEmployees.length === 0 && (
        <div className="py-20 text-center">
          <div className="w-20 h-20 bg-slate-100 rounded-[2rem] flex items-center justify-center mx-auto text-slate-300 mb-6"><Search size={40} /></div>
          <h3 className="text-lg font-black text-slate-800">{t('employees.noResults')}</h3>
          <p className="text-slate-500 text-sm font-medium mt-2">{t('employees.noResultsDesc')}</p>
        </div>
      )}
    </div>
  );
};

export default Employees;
